import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

/**
 * PTZControl — video preview + luma-waveform + manuelle PTZ-knapper.
 * Pan/tilt/zoom rutes via den lokale VISCA-broen (visca_bridge.py, port 52380)
 * med samme kamera-IP som Autotrack-fanen (localStorage: autotrack-camera-ip).
 */
const BRIDGE = "http://127.0.0.1:52380";
const PTZ_SPEEDS = {
  1: { label: "Rolig", pan: "04", tilt: "04", zi: "8101040721FF", zo: "8101040731FF" },
  2: { label: "Normal", pan: "0C", tilt: "0C", zi: "8101040724FF", zo: "8101040734FF" },
  3: { label: "Rask", pan: "18", tilt: "14", zi: "8101040727FF", zo: "8101040737FF" },
};

export default function PTZControl() {
  return <PTZControlImpl />;
}

export { PTZControlImpl as PTZControl };

function PTZControlImpl() {
  // Refs
  const videoRef = useRef(null);
  const waveformCanvasRef = useRef(null);
  const waveformAnimRef = useRef(null);
  const analyzeCanvasRef = useRef(null);
  const streamRef = useRef(null);

  // State
  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedVideoDevice, setSelectedVideoDevice] = useState(
    () => localStorage.getItem("ptz_video_device") || ""
  );
  const [videoActive, setVideoActive] = useState(false);

  // PTZ-styring via lokal bro
  const [bridgeOk, setBridgeOk] = useState(false);
  const [cameraIp, setCameraIp] = useState(() => {
    const v = localStorage.getItem("autotrack-camera-ip");
    return (!v || v === "192.168.1.100") ? "10.52.49.254" : v;
  });
  const [ptzSpeed, setPtzSpeed] = useState(2);
  const ptzSpeedRef = useRef(2);
  const [lastSend, setLastSend] = useState(null);
  useEffect(() => { ptzSpeedRef.current = ptzSpeed; }, [ptzSpeed]);

  useEffect(() => {
    let alive = true;
    const check = async () => {
      try {
        const r = await fetch(`${BRIDGE}/health`, { signal: AbortSignal.timeout(2500) });
        if (alive) setBridgeOk(r.ok);
      } catch {
        if (alive) setBridgeOk(false);
      }
    };
    check();
    const t = setInterval(check, 5000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Synkroniser VISCA-sekvensnr én gang pr. IP når broen er tilkoblet.
  // Uten dette ignorerer kameraet kommandoer etter en omstart av broen.
  const resetIpRef = useRef("");
  useEffect(() => {
    const ip = cameraIp.trim();
    if (!bridgeOk || !ip || resetIpRef.current === ip) return;
    resetIpRef.current = ip;
    fetch(`${BRIDGE}/reset`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ip, port: 52381 }),
    }).catch(() => {});
  }, [bridgeOk, cameraIp]);

  const sendVisca = async (hex) => {
    const ip = cameraIp.trim();
    try {
      const r = await fetch(`${BRIDGE}/visca`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ip, port: 52381, hex }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.ok !== false) setLastSend({ ok: true, ip });
      else setLastSend({ ok: false, msg: d.error || `broen svarte ${r.status}` });
    } catch {
      setLastSend({ ok: false, msg: "ingen kontakt med broen (kjører visca_bridge.py?)" });
    }
  };
  const drive = (p, t) => {
    const s = PTZ_SPEEDS[ptzSpeedRef.current];
    sendVisca(`81010601${s.pan}${s.tilt}0${p}0${t}FF`);
  };
  const stopMove = () => sendVisca("8101060118180303FF");
  const stopZoom = () => sendVisca("8101040700FF");

  const holdBtn = (label, onStart, onStop, testId) => (
    <button
      data-testid={testId}
      onPointerDown={(e) => { e.preventDefault(); onStart(); }}
      onPointerUp={onStop}
      onPointerLeave={onStop}
      onPointerCancel={onStop}
      onContextMenu={(e) => e.preventDefault()}
      style={{
        height: "34px", borderRadius: "6px", cursor: "pointer",
        border: "1px solid #334155", backgroundColor: "#1E293B", color: "#E2E8F0",
        fontSize: "0.85rem", touchAction: "none", userSelect: "none",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      {label}
    </button>
  );

  // Enumerate video input devices
  useEffect(() => {
    navigator.mediaDevices?.enumerateDevices()
      .then(devices => {
        setVideoDevices(devices.filter(d => d.kind === "videoinput"));
      })
      .catch(() => {});
  }, []);

  // Start/stop video stream when device changes
  useEffect(() => {
    if (!selectedVideoDevice) {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
      setVideoActive(false);
      return;
    }
    localStorage.setItem("ptz_video_device", selectedVideoDevice);
    navigator.mediaDevices.getUserMedia({
      video: {
        deviceId: { exact: selectedVideoDevice },
        width: { ideal: 320 },
        height: { ideal: 180 },
      },
    }).then(stream => {
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setVideoActive(true);
    }).catch(err => {
      toast.error(`Video-feil: ${err.message}`);
      setVideoActive(false);
    });
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    };
  }, [selectedVideoDevice]);

  // Waveform monitor — luma waveform overlay on video
  useEffect(() => {
    if (!videoActive) {
      if (waveformAnimRef.current) {
        cancelAnimationFrame(waveformAnimRef.current);
        waveformAnimRef.current = null;
      }
      return;
    }
    if (!analyzeCanvasRef.current) {
      analyzeCanvasRef.current = document.createElement("canvas");
    }

    let lastDraw = 0;
    const drawWaveform = (timestamp) => {
      waveformAnimRef.current = requestAnimationFrame(drawWaveform);
      // Throttle to ~12fps
      if (timestamp - lastDraw < 80) return;
      lastDraw = timestamp;

      const video = videoRef.current;
      const wfCanvas = waveformCanvasRef.current;
      if (!video || !wfCanvas || video.readyState < 2) return;

      const aCanvas = analyzeCanvasRef.current;
      const sampleW = 120;
      const sampleH = 68;
      aCanvas.width = sampleW;
      aCanvas.height = sampleH;
      const aCtx = aCanvas.getContext("2d", { willReadFrequently: true });
      aCtx.drawImage(video, 0, 0, sampleW, sampleH);

      let imgData;
      try {
        imgData = aCtx.getImageData(0, 0, sampleW, sampleH);
      } catch { return; }

      const pixels = imgData.data;
      const wfW = wfCanvas.width;
      const wfH = wfCanvas.height;
      const ctx = wfCanvas.getContext("2d");

      ctx.fillStyle = "rgba(0, 0, 0, 0.85)";
      ctx.fillRect(0, 0, wfW, wfH);

      ctx.strokeStyle = "rgba(255, 255, 255, 0.12)";
      ctx.lineWidth = 1;
      [0, 0.5, 1].forEach(pct => {
        const y = Math.round(wfH * (1 - pct));
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(wfW, y);
        ctx.stroke();
      });

      const lumaLevels = 64;
      const histogram = new Uint8Array(sampleW * lumaLevels);
      for (let x = 0; x < sampleW; x++) {
        for (let y = 0; y < sampleH; y++) {
          const idx = (y * sampleW + x) * 4;
          // BT.709 luma
          const luma = (pixels[idx] * 0.2126 + pixels[idx + 1] * 0.7152 + pixels[idx + 2] * 0.0722) / 255;
          const lumaBin = Math.min(Math.floor(luma * lumaLevels), lumaLevels - 1);
          histogram[x * lumaLevels + lumaBin]++;
        }
      }

      const colW = wfW / sampleW;
      for (let x = 0; x < sampleW; x++) {
        for (let lvl = 0; lvl < lumaLevels; lvl++) {
          const count = histogram[x * lumaLevels + lvl];
          if (count === 0) continue;
          const intensity = Math.min(count / (sampleH * 0.15), 1);
          const drawY = wfH - ((lvl + 0.5) / lumaLevels) * wfH;
          const drawX = x * colW;
          const alpha = 0.15 + intensity * 0.85;
          const green = Math.floor(120 + intensity * 135);
          ctx.fillStyle = `rgba(40, ${green}, 60, ${alpha})`;
          ctx.fillRect(drawX, drawY - 0.5, Math.max(colW, 1), 1.5);
        }
      }
    };

    waveformAnimRef.current = requestAnimationFrame(drawWaveform);
    return () => {
      if (waveformAnimRef.current) {
        cancelAnimationFrame(waveformAnimRef.current);
        waveformAnimRef.current = null;
      }
    };
  }, [videoActive]);

  return (
    <div
      className="p-2 rounded-lg bg-slate-900 border border-slate-700"
      data-testid="camera-preview"
    >
      <div style={{ marginBottom: "6px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
          <span style={{ color: "#94A3B8", fontSize: "0.7rem", whiteSpace: "nowrap", fontWeight: 600 }}>
            Kamerabilde:
          </span>
          <select
            data-testid="ptz-video-source"
            value={selectedVideoDevice}
            onChange={(e) => setSelectedVideoDevice(e.target.value)}
            style={{
              flex: 1, fontSize: "0.7rem", padding: "4px 6px", borderRadius: "4px",
              background: "#1E293B", color: "#E2E8F0", border: "1px solid #334155",
              overflow: "hidden", textOverflow: "ellipsis",
            }}
          >
            <option value="">Velg kilde…</option>
            {videoDevices.map((d, i) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label || `Kamera ${i + 1}`}
              </option>
            ))}
          </select>
        </div>
        {videoActive && (
          <div
            style={{
              position: "relative", width: "100%",
              borderRadius: "4px", overflow: "hidden",
              border: "1px solid #334155",
            }}
          >
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{ width: "100%", display: "block", borderRadius: "4px" }}
              data-testid="ptz-video-preview"
            />
            <canvas
              ref={waveformCanvasRef}
              width={120}
              height={48}
              data-testid="ptz-waveform"
              style={{
                position: "absolute",
                bottom: "4px", left: "4px",
                width: "40%", height: "auto",
                borderRadius: "3px", pointerEvents: "none",
                border: "1px solid rgba(255,255,255,0.15)",
              }}
            />
            <div
              style={{
                position: "absolute", top: "2px", right: "4px",
                fontSize: "0.55rem", color: "#94A3B8",
                background: "rgba(0,0,0,0.6)",
                padding: "1px 4px", borderRadius: "2px",
              }}
            >
              LIVE
            </div>
          </div>
        )}
        {!videoActive && !selectedVideoDevice && (
          <div
            style={{
              padding: "24px 12px", textAlign: "center",
              color: "#64748B", fontSize: "0.75rem", fontStyle: "italic",
              border: "1px dashed #334155", borderRadius: "4px",
            }}
          >
            Velg en videokilde over for å vise preview og waveform
          </div>
        )}
      </div>

      {/* Manuell PTZ-styring via lokal bro */}
      <div style={{ borderTop: "1px solid #334155", paddingTop: "8px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
          <span
            data-testid="ptz-bridge-dot"
            style={{
              width: "8px", height: "8px", borderRadius: "50%", flexShrink: 0,
              backgroundColor: bridgeOk ? "#22C55E" : "#DC2626",
            }}
          />
          <span style={{ color: "#94A3B8", fontSize: "0.65rem", fontWeight: 600, flex: 1 }}>
            PTZ {bridgeOk ? "· bro tilkoblet" : "· bro ikke funnet (start visca_bridge.py)"}
          </span>
          <input
            data-testid="ptz-camera-ip"
            value={cameraIp}
            onChange={(e) => {
              setCameraIp(e.target.value);
              localStorage.setItem("autotrack-camera-ip", e.target.value);
            }}
            placeholder="Kamera-IP"
            style={{
              width: "104px", fontSize: "0.65rem", padding: "3px 6px", borderRadius: "4px",
              background: "#1E293B", color: "#E2E8F0", border: "1px solid #334155",
              fontFamily: "'JetBrains Mono', monospace",
            }}
          />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "5px", marginBottom: "6px" }}>
          {holdBtn("−", () => sendVisca(PTZ_SPEEDS[ptzSpeedRef.current].zo), stopZoom, "ptz-zoom-out")}
          {holdBtn("▲", () => drive(3, 1), stopMove, "ptz-tilt-up")}
          {holdBtn("+", () => sendVisca(PTZ_SPEEDS[ptzSpeedRef.current].zi), stopZoom, "ptz-zoom-in")}
          {holdBtn("◀", () => drive(1, 3), stopMove, "ptz-pan-left")}
          {holdBtn("▼", () => drive(3, 2), stopMove, "ptz-tilt-down")}
          {holdBtn("▶", () => drive(2, 3), stopMove, "ptz-pan-right")}
        </div>
        <div style={{ display: "flex", gap: "5px" }}>
          {Object.entries(PTZ_SPEEDS).map(([lvl, s]) => (
            <button
              key={lvl}
              data-testid={`ptz-speed-${lvl}`}
              onClick={() => setPtzSpeed(parseInt(lvl, 10))}
              style={{
                flex: 1, padding: "4px", borderRadius: "999px", cursor: "pointer", fontSize: "0.6rem",
                fontWeight: 700,
                border: ptzSpeed === parseInt(lvl, 10) ? "1.5px solid #5CE0D6" : "1px solid #334155",
                backgroundColor: ptzSpeed === parseInt(lvl, 10) ? "rgba(92,224,214,0.12)" : "transparent",
                color: ptzSpeed === parseInt(lvl, 10) ? "#5CE0D6" : "#64748B",
              }}
            >
              {s.label}
            </button>
          ))}
          <button
            data-testid="ptz-goto-home"
            onClick={() => sendVisca("8101043F0200FF")}
            title="Gå til bordposisjon (preset 0)"
            style={{
              flex: 1, padding: "4px", borderRadius: "999px", cursor: "pointer", fontSize: "0.6rem",
              fontWeight: 700, border: "1px solid #334155", backgroundColor: "transparent", color: "#5CE0D6",
            }}
          >
            Bord
          </button>
        </div>
        {lastSend && (
          <div
            data-testid="ptz-send-status"
            style={{
              marginTop: "6px", fontSize: "0.62rem", fontFamily: "'JetBrains Mono', monospace",
              color: lastSend.ok ? "#4ADE80" : "#F87171",
            }}
          >
            {lastSend.ok ? `✓ Sendt til kamera ${lastSend.ip}` : `✕ ${lastSend.msg}`}
          </div>
        )}
      </div>
    </div>
  );
}
